import { useState } from "react";

import {
  useAdminOrganizations,
  useOrganizationUsage,
  useSetOrganizationStatus,
} from "../../api/queries";
import type { AdminOrganization } from "../../api/types";
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Loading,
  Modal,
} from "../../ui/primitives";

export function AdminOrganizationsPage() {
  const organizations = useAdminOrganizations();
  const setStatus = useSetOrganizationStatus();
  const [inspecting, setInspecting] = useState<AdminOrganization | null>(null);

  if (organizations.isLoading) return <Loading />;
  if (organizations.error) return <ErrorNote error={organizations.error} />;

  return (
    <div className="space-y-4">
      {setStatus.error != null && <ErrorNote error={setStatus.error} />}

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-muted">
              <th scope="col" className="py-2 font-medium">Organization</th>
              <th scope="col" className="font-medium">Members</th>
              <th scope="col" className="font-medium">Status</th>
              <th scope="col" className="font-medium">Created</th>
              <th scope="col" className="sr-only">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {(organizations.data ?? []).map((organization) => (
              <tr key={organization.id}>
                <td className="py-2">
                  <div className="font-medium">{organization.name}</div>
                  <div className="text-xs text-muted">{organization.slug}</div>
                </td>
                <td className="tabular-nums">{organization.member_count}</td>
                <td>
                  {organization.status === "active" ? (
                    <Badge tone="good">Active</Badge>
                  ) : (
                    <Badge tone="bad">Suspended</Badge>
                  )}
                </td>
                <td className="text-muted">
                  {new Date(organization.created_at).toLocaleDateString()}
                </td>
                <td className="py-2 text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setInspecting(organization)}>
                      Usage
                    </Button>
                    <Button
                      size="sm"
                      variant={organization.status === "active" ? "danger" : "secondary"}
                      disabled={setStatus.isPending}
                      onClick={() =>
                        setStatus.mutate({
                          orgId: organization.id,
                          suspend: organization.status === "active",
                        })
                      }
                    >
                      {organization.status === "active" ? "Suspend" : "Reinstate"}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <UsageDialog organization={inspecting} onClose={() => setInspecting(null)} />
    </div>
  );
}

function UsageDialog({
  organization,
  onClose,
}: {
  organization: AdminOrganization | null;
  onClose: () => void;
}) {
  const usage = useOrganizationUsage(organization?.id ?? "");

  return (
    <Modal
      open={organization !== null}
      onClose={onClose}
      title={organization ? `${organization.name} — usage` : "Usage"}
    >
      {usage.isLoading ? (
        <Loading />
      ) : usage.error ? (
        <ErrorNote error={usage.error} />
      ) : usage.data ? (
        <div className="space-y-3">
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <dt className="text-muted">Sites</dt>
            <dd className="tabular-nums">{usage.data.sites}</dd>
            <dt className="text-muted">Documents</dt>
            <dd className="tabular-nums">{usage.data.documents}</dd>
            <dt className="text-muted">Conversations</dt>
            <dd className="tabular-nums">{usage.data.conversations}</dd>
            <dt className="text-muted">Messages</dt>
            <dd className="tabular-nums">{usage.data.messages}</dd>
            <dt className="text-muted">Tokens in / out</dt>
            <dd className="tabular-nums">
              {usage.data.tokens_in.toLocaleString()} /{" "}
              {usage.data.tokens_out.toLocaleString()}
            </dd>
            <dt className="text-muted">AI cost</dt>
            <dd className="tabular-nums">
              ${Number(usage.data.total_cost_usd).toFixed(4)}
            </dd>
          </dl>

          <p className="text-xs text-muted">
            Lifetime totals. Billing periods and quotas arrive with metering.
          </p>

          <div className="flex justify-end">
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
